import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { ManagementCompany } from './useData';
import { ManagementCompanyLogoPanel } from './LogoPanel';

export const moduleConfig = {
  module: {
    title: 'Management companies',
    permissionModuleKey: 'management_companies',
    route: {
      list: '/parameters/management-companies',
      new: '/parameters/management-companies/new',
      detail: (id: number) => `/parameters/management-companies/${id}`,
      edit: (id: number) => `/parameters/management-companies/${id}/edit`,
    },
    api: {
      list: '/management-companies',
      detail: (id: number) => `/management-companies/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: { placeholder: 'Search by code or name…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['name', 'isActive'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'All names',
        getValue: (item: ManagementCompany) => item.name,
        options: [],
      },
      {
        key: 'isActive',
        label: 'Active',
        type: 'boolean' as const,
        getValue: (item: ManagementCompany) => (item.isActive ? 'Yes' : 'No'),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ],
      },
    ],
    applyFilter: (items: ManagementCompany[], filters: Record<string, string[]>) => {
      return items.filter((item) => {
        if (filters.name?.length) {
          const v = item.name || '';
          if (!filters.name.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }
        if (filters.isActive?.length) {
          const s = item.isActive ? 'Yes' : 'No';
          if (!filters.isActive.includes(s)) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New management company',
        showTitle: false,
        variant: 'primary' as const,
        href: '/parameters/management-companies/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: ManagementCompany) => item.id,
  },
  emptyStates: {
    noData: 'Add a management company to assign as vessel manager.',
    noSearchResults: 'No management companies match your search.',
    noFilterResults: 'No management companies match the filters.',
  },
  delete: {
    title: 'Delete management company',
    message: 'Delete this management company? Linked vessels will have manager cleared.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete management company.',
    handler: async (id: number) => {
      const { deleteManagementCompany } = await import('../../../services/mutations');
      await deleteManagementCompany(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : ''),
  },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This management company does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Management company',
    getTitle: (item: ManagementCompany) =>
      item.name ? item.name : `Management company #${item.id}`,
  },
  form: {
    new: {
      title: 'New management company',
      subtitle: 'Create a ship management company for the fleet register',
      submitText: 'Create',
    },
    edit: { title: 'Edit management company', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    create: async (data: Partial<ManagementCompany>) => {
      const { createManagementCompany } = await import('../../../services/mutations');
      return createManagementCompany(data);
    },
    update: async (id: number, data: Partial<ManagementCompany>) => {
      const { updateManagementCompany } = await import('../../../services/mutations');
      return updateManagementCompany(id, data);
    },
  },
  fields: [
    {
      name: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      placeholder: 'e.g. ATLAS',
      validation: { required: true, minLength: 1, maxLength: 30 },
    },
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'e.g. Atlas Ship Management',
      validation: { required: true, minLength: 1, maxLength: 200 },
    },
    {
      name: 'sortOrder',
      label: 'Sort order',
      type: 'number',
      defaultValue: 0,
    },
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Active',
      defaultValue: true,
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Optional notes',
      validation: { maxLength: 500 },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'main',
        title: 'Management company',
        icon: '',
        gridCols: 'md:grid-cols-12',
        fields: ['code', 'name', 'sortOrder', 'isActive', 'notes'],
        fieldSpans: {
          code: 'md:col-span-3',
          name: 'md:col-span-9',
          sortOrder: 'md:col-span-3',
          isActive: 'md:col-span-6',
          notes: 'md:col-span-12',
        },
        collapsible: false,
      },
    ],
  } as FormLayout,
};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

export const getColumns = (
  formatDate: (dateString?: string) => string,
): Column<ManagementCompany>[] => [
  { key: 'code', header: 'Code', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v} /> },
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v, row) => (
      <Badge
        variant={v ?? row?.isActive ? 'success' : 'danger'}
        className="shrink-0 border rounded-md px-2 py-0.5"
      >
        {v ?? row?.isActive ? 'Yes' : 'No'}
      </Badge>
    ),
  },
  {
    key: 'sortOrder',
    header: 'Order',
    sortable: true,
    render: (v) => <TextCell value={v != null ? String(v) : undefined} />,
  },
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

export const getDetailSections = (
  item: ManagementCompany,
  formatDate: (s?: string) => string,
  _formatCurrency?: (amount?: number | null) => string,
  mutate?: () => void,
) => [
  {
    title: 'Logo',
    content: (
      <ManagementCompanyLogoPanel
        companyId={item.id}
        logoStorageKey={item.logoStorageKey}
        logoOriginalFilename={item.logoOriginalFilename}
        onChanged={() => mutate?.()}
        editable
      />
    ),
  },
  {
    title: 'Overview',
    fields: [
      { label: 'Code', value: item.code },
      { label: 'Name', value: item.name },
      { label: 'Sort order', value: String(item.sortOrder) },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Notes', value: item.notes || '—' },
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
    ],
  },
];

const TableActionsCell: React.FC<{
  item: ManagementCompany;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => (
  <div className="flex items-center flex-nowrap">
    <Link
      to={moduleConfig.module.route.detail(item.id)}
      className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
      onClick={(e) => e.stopPropagation()}
      title="View"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      </svg>
    </Link>
    {canEdit && (
      <Link
        to={moduleConfig.module.route.edit(item.id)}
        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="Edit"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      </Link>
    )}
    {canDelete && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      </button>
    )}
  </div>
);

export const getTableActions = (
  item: ManagementCompany,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell
    item={item}
    onDelete={onDelete}
    canDelete={options?.canDelete !== false}
    canEdit={options?.canEdit !== false}
  />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

export const getStatusBadges = () => null;

export const getInitialFormData = (): Partial<ManagementCompany> => ({
  code: '',
  name: '',
  sortOrder: 0,
  isActive: true,
  notes: '',
});

export const mapItemToFormData = (item: ManagementCompany): Partial<ManagementCompany> => ({
  code: item.code,
  name: item.name,
  sortOrder: item.sortOrder,
  isActive: item.isActive ?? true,
  notes: item.notes ?? '',
});

export type ModuleConfig = typeof moduleConfig;
